import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'land_clearing', label: 'Land Clearing' },
  { value: 'mulching', label: 'Mulching' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'grading', label: 'Grading' },
  { value: 'brush_removal', label: 'Brush Removal' },
  { value: 'tree_removal', label: 'Tree Removal' },
  { value: 'site_prep', label: 'Site Preparation' }
];

export const CreateJobPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    acreage: '',
    location: '',
    address: '',
    budget_min: '',
    budget_max: '',
    deadline: '',
    requirements: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        acreage: parseFloat(formData.acreage),
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        requirements: formData.requirements ? formData.requirements.split('\n').filter(r => r.trim()) : []
      };

      await axios.post(`${API}/jobs`, payload);
      toast.success('Job posted successfully!');
      navigate('/customer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950" data-testid="create-job-page">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/customer/dashboard" 
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </Link>

        <h1 className="font-heading text-4xl text-white uppercase mb-8">Post a New Job</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="industrial-card p-6 space-y-6">
            <h2 className="font-heading text-xl text-orange-500 uppercase">Job Details</h2>

            <div>
              <Label htmlFor="title" className="form-label">Job Title</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., 10 Acre Land Clearing for Home Site"
                className="mt-2 bg-stone-900 border-stone-700 h-12"
                data-testid="job-title"
                required
              />
            </div>

            <div>
              <Label htmlFor="category" className="form-label">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })} required>
                <SelectTrigger className="mt-2 bg-stone-900 border-stone-700 h-12" data-testid="job-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description" className="form-label">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the work needed, terrain type, access roads, any obstacles..."
                className="mt-2 bg-stone-900 border-stone-700 min-h-32"
                data-testid="job-description"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="acreage" className="form-label">Acreage</Label>
                <Input
                  id="acreage"
                  name="acreage"
                  type="number"
                  step="0.1"
                  value={formData.acreage}
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="job-acreage"
                  required
                />
              </div>
              <div>
                <Label htmlFor="deadline" className="form-label">Target Completion Date</Label>
                <Input
                  id="deadline"
                  name="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={handleChange}
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="job-deadline"
                />
              </div>
            </div>
          </div>

          <div className="industrial-card p-6 space-y-6">
            <h2 className="font-heading text-xl text-orange-500 uppercase">Location</h2>

            <div>
              <Label htmlFor="location" className="form-label">City/State</Label>
              <Input
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., Austin, TX"
                className="mt-2 bg-stone-900 border-stone-700 h-12"
                data-testid="job-location"
                required
              />
            </div>

            <div>
              <Label htmlFor="address" className="form-label">Property Address (Optional)</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street address or GPS coordinates"
                className="mt-2 bg-stone-900 border-stone-700 h-12"
                data-testid="job-address"
              />
            </div>
          </div>

          <div className="industrial-card p-6 space-y-6">
            <h2 className="font-heading text-xl text-orange-500 uppercase">Budget</h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="budget_min" className="form-label">Minimum Budget ($)</Label>
                <Input
                  id="budget_min"
                  name="budget_min"
                  type="number"
                  value={formData.budget_min}
                  onChange={handleChange}
                  placeholder="e.g., 5000"
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="job-budget-min"
                />
              </div>
              <div>
                <Label htmlFor="budget_max" className="form-label">Maximum Budget ($)</Label>
                <Input
                  id="budget_max"
                  name="budget_max"
                  type="number"
                  value={formData.budget_max}
                  onChange={handleChange}
                  placeholder="e.g., 10000"
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="job-budget-max"
                />
              </div>
            </div>
          </div>

          <div className="industrial-card p-6 space-y-6">
            <h2 className="font-heading text-xl text-orange-500 uppercase">Requirements</h2>

            <div>
              <Label htmlFor="requirements" className="form-label">Special Requirements (One per line)</Label>
              <Textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="Must have insurance&#10;Forestry mulcher required&#10;Weekend work preferred"
                className="mt-2 bg-stone-900 border-stone-700 min-h-32"
                data-testid="job-requirements"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            data-testid="submit-job-btn"
            className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide text-lg rounded-sm"
          >
            {loading ? <Loader2 className="animate-spin" /> : 'Post Job'}
          </Button>
        </form>
      </main>
    </div>
  );
};
